import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, seededRandom } from "../spec";

const SHEET = "TX Clientes";
const TABLE = "ClientesSucios";
const NBSP = " ";

const CIUDADES = ["Monterrey", "Guadalajara", "Puebla", "Querétaro", "León"] as const;

const rnd = seededRandom(2020);

/** Messes up a clean name: random case and extra spaces around and inside. */
function ensuciarNombre(nombre: string): string {
  const [first, last] = nombre.split(" ");
  const caso = (w: string) => (rnd.next() < 0.5 ? w.toUpperCase() : w.toLowerCase());
  const gap = " ".repeat(rnd.int(1, 3));
  return `${" ".repeat(rnd.int(0, 2))}${caso(first)}${gap}${caso(last)}${" ".repeat(rnd.int(0, 2))}`;
}

function ensuciarTelefono(digits: string): string {
  const [a, b, c] = [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6)];
  switch (rnd.int(0, 3)) {
    case 0: return `(${a}) ${b}-${c}`;
    case 1: return `${a}.${b}.${c}`;
    case 2: return `${a} ${b} ${c}`;
    default: return `${a}-${b}-${c}`;
  }
}

function ensuciarCiudad(ciudad: string): string {
  const caso = rnd.int(0, 2) === 0 ? ciudad.toUpperCase() : rnd.next() < 0.5 ? ciudad.toLowerCase() : ciudad;
  // Text pasted from the web brings non-breaking spaces that ESPACIOS ignores.
  return rnd.next() < 0.45 ? `${caso}${NBSP}` : rnd.next() < 0.5 ? ` ${caso} ` : caso;
}

const usuario = (n: string) =>
  n.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(" ", ".");

// Nombre, Teléfono, Correo, Ciudad, Saldo
const BASE = NOMBRES.slice(0, 30).map((nombre) => {
  const digits = `${rnd.pick(["81", "33", "22", "44", "47"])}${rnd.int(10000000, 99999999)}`;
  const ciudad = rnd.pick(CIUDADES);
  const saldo = rnd.int(3, 950) * 50;
  return { nombre, digits, ciudad, saldo };
});

const ROWS = BASE.map((b) => [
  ensuciarNombre(b.nombre),
  ensuciarTelefono(b.digits),
  ` ${usuario(b.nombre).toUpperCase().replace("@", "")}@CORREO.MX `.replace(/^ /, rnd.next() < 0.5 ? " " : ""),
  ensuciarCiudad(b.ciudad),
  ` $${b.saldo.toLocaleString("en-US")} `,
] as [string, string, string, string, string]);

type Row = (typeof ROWS)[number];
const trim = (s: string) => s.replace(/ +/g, " ").trim();
const proper = (s: string) =>
  s.toLowerCase().replace(/(^|[^\p{L}])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());

export const spec: FuncionProjectSpec = {
  projectId: "funciones-limpiar",
  sheets: [
    {
      name: SHEET,
      title: "Base de clientes sucia",
      subtitle: "Exportación del sistema anterior — mayúsculas mezcladas, espacios de más y números con símbolos",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Nombre", "Teléfono", "Correo", "Ciudad", "Saldo"],
          rows: ROWS,
          textColumns: ["Nombre", "Teléfono", "Correo", "Ciudad", "Saldo"],
          answerColumns: [
            "Nombre sin espacios",
            "Nombre propio",
            "Correo limpio",
            "Teléfono 10 dígitos",
            "Saldo número",
            "Ciudad limpia",
            "Iniciales",
          ],
          formats: { "Saldo número": "$#,##0" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nombre sin espacios", fns: ["TRIM"], exact: true,
      expected: ROWS.map((r: Row) => trim(r[0])),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nombre propio", fns: ["PROPER", "TRIM"], exact: true,
      expected: ROWS.map((r) => proper(trim(r[0]))),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Correo limpio", fns: ["LOWER", "TRIM"], exact: true,
      expected: ROWS.map((r) => trim(r[2]).toLowerCase()),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Teléfono 10 dígitos", fns: ["SUBSTITUTE"],
      minCounts: { SUBSTITUTE: 3 }, exact: true,
      expected: BASE.map((b) => b.digits),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Saldo número", fns: ["VALUE", "SUBSTITUTE"],
      expected: BASE.map((b) => b.saldo),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Ciudad limpia", fns: ["PROPER", "TRIM", "SUBSTITUTE"],
      exact: true,
      expected: ROWS.map((r) => proper(trim(r[3].replace(new RegExp(NBSP, "g"), " ")))),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Iniciales", fns: ["LEFT", "MID", "SEARCH"], exact: true,
      expected: BASE.map((b) => {
        const [first, last] = b.nombre.split(" ");
        return `${first[0]}${last[0]}`;
      }),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-limpiar",
  title: "ESPACIOS, NOMPROPIO y SUSTITUIR: Base de clientes sucia",
  subtitle: "Dejar el texto listo para usarse",
  description:
    "Te entregan la base de clientes exportada de un sistema viejo: nombres con espacios de más y mayúsculas al azar, teléfonos escritos de cuatro formas distintas, saldos como \" $12,500 \" que Excel no puede sumar y ciudades copiadas de una página web con un espacio invisible.\n\nAquí aprenderás las funciones que más se usan para limpiar datos y la trampa más común: el espacio duro CARACTER(160).",
  topics: [
    "ESPACIOS: quitar espacios sobrantes",
    "NOMPROPIO, MINUSC y MAYUSC",
    "SUSTITUIR anidado para quitar símbolos",
    "Texto con símbolos → número con VALOR",
    "El espacio duro CARACTER(160)",
    "Iniciales con IZQUIERDA, EXTRAE y HALLAR",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Quitar espacios de más",
      description:
        "En la hoja <strong>TX Clientes</strong>, columna <strong>Nombre sin espacios</strong>: quita los espacios del inicio, del final y los espacios dobles entre palabras. <strong>No cambies</strong> las mayúsculas todavía.",
      solution: [
        `Selecciona ${F("F5")}.`,
        "ESPACIOS deja un solo espacio entre palabras y quita los de los extremos.",
        `Escribe ${F("=ESPACIOS([@Nombre])")}.`,
      ],
      expectedResult: "\"ANA lópez\" en lugar de \"  ANA   lópez \"",
      finalFormula: "=ESPACIOS([@Nombre])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Nombre con mayúscula inicial",
      description:
        "En la columna <strong>Nombre propio</strong>: deja el nombre sin espacios de más <strong>y</strong> con la primera letra de cada palabra en mayúscula: <strong>Ana López</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        "NOMPROPIO pone en mayúscula la primera letra de cada palabra y el resto en minúscula. Anídala con ESPACIOS.",
        `Escribe ${F("=NOMPROPIO(ESPACIOS([@Nombre]))")}.`,
      ],
      expectedResult: "Ana López",
      finalFormula: "=NOMPROPIO(ESPACIOS([@Nombre]))",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Correo limpio",
      description:
        "En la columna <strong>Correo limpio</strong>: los correos vienen en MAYÚSCULAS y con espacios. Déjalos <strong>en minúsculas</strong> y sin espacios en los extremos.",
      solution: [`Selecciona ${F("H5")}.`, `Escribe ${F("=MINUSC(ESPACIOS([@Correo]))")}.`],
      expectedResult: "ana.lopez@correo.mx",
      finalFormula: "=MINUSC(ESPACIOS([@Correo]))",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Teléfono de 10 dígitos",
      description: conditions(
        "En la columna <strong>Teléfono 10 dígitos</strong>: los teléfonos vienen en formatos distintos. Deja <strong>solo los 10 dígitos</strong>, quitando todos estos caracteres:",
        [
          "Paréntesis <strong>(</strong> y <strong>)</strong>",
          "Guiones <strong>-</strong> y puntos <strong>.</strong>",
          "Espacios",
        ]
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        `SUSTITUIR(texto, lo que buscas, lo que pones) reemplaza todas las apariciones. Con ${F('""')} lo borra.`,
        "Para quitar varios caracteres, anida un SUSTITUIR dentro de otro: el de adentro se ejecuta primero.",
        `Escribe ${F('=SUSTITUIR(SUSTITUIR(SUSTITUIR(SUSTITUIR(SUSTITUIR([@Teléfono],"(",""),")",""),"-",""),".","")," ","")')}.`,
      ],
      expectedResult: "8112345678",
      finalFormula:
        '=SUSTITUIR(SUSTITUIR(SUSTITUIR(SUSTITUIR(SUSTITUIR([@Teléfono],"(",""),")",""),"-",""),".","")," ","")',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Saldo como número",
      description: conditions(
        "En la columna <strong>Saldo número</strong>: el saldo viene como texto (\" $12,500 \") y no se puede sumar. Conviértelo en número:",
        [
          "Quita el signo <strong>$</strong>",
          "Quita la coma de los miles <strong>,</strong>",
          "Convierte el resultado con <strong>VALOR</strong> (VALOR ignora los espacios de los extremos)",
        ]
      ),
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F('=VALOR(SUSTITUIR(SUSTITUIR([@Saldo],"$",""),",",""))')}.`,
        "Comprueba con SUMA: antes daba 0, ahora da el total real.",
      ],
      expectedResult: "12500 (número)",
      finalFormula: '=VALOR(SUSTITUIR(SUSTITUIR([@Saldo],"$",""),",",""))',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Ciudad con espacio invisible",
      description: conditions(
        "En la columna <strong>Ciudad limpia</strong>: deja cada ciudad como <strong>Monterrey</strong>, <strong>Querétaro</strong>… Algunas se copiaron de una página web y traen un <strong>espacio duro</strong> que ESPACIOS no quita:",
        [
          "Cambia el espacio duro <strong>CARACTER(160)</strong> por un espacio normal con SUSTITUIR",
          "Luego quita los espacios con ESPACIOS",
          "Al final aplica NOMPROPIO",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `Prueba primero ${F("=NOMPROPIO(ESPACIOS([@Ciudad]))")}: verás que \"Monterrey \" sigue con un espacio al final en algunas filas. Es el carácter 160.`,
        `Escribe ${F('=NOMPROPIO(ESPACIOS(SUSTITUIR([@Ciudad],CARACTER(160)," ")))')}.`,
      ],
      expectedResult: "Monterrey, Guadalajara, Querétaro…",
      finalFormula: '=NOMPROPIO(ESPACIOS(SUSTITUIR([@Ciudad],CARACTER(160)," ")))',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Iniciales",
      description: conditions(
        "En la columna <strong>Iniciales</strong>: obtén las iniciales en mayúscula a partir del <strong>Nombre propio</strong> de la Tarea 2: <strong>Ana López → AL</strong>.",
        [
          "La primera inicial es el <strong>primer carácter</strong>",
          "La segunda es el carácter que está <strong>justo después del espacio</strong>; <strong>HALLAR</strong> te dice en qué posición está el espacio",
        ]
      ),
      solution: [
        `Selecciona ${F("L5")}.`,
        `${F('HALLAR(" ",[@[Nombre propio]])')} devuelve la posición del espacio; la segunda inicial está en esa posición + 1.`,
        `Escribe ${F('=IZQUIERDA([@[Nombre propio]],1)&EXTRAE([@[Nombre propio]],HALLAR(" ",[@[Nombre propio]])+1,1)')}.`,
      ],
      expectedResult: "AL",
      finalFormula: '=IZQUIERDA([@[Nombre propio]],1)&EXTRAE([@[Nombre propio]],HALLAR(" ",[@[Nombre propio]])+1,1)',
    },
  ],
};
